import isEmpty from 'lodash-es/isEmpty';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { DialogType } from '../../../@types';
import { UserRole } from '../../../@types/api';
import { BETTING_ACTIVITY_OPTIONS, PARTNER_TYPE_OPTIONS, QUERY_KEYS } from '../../../constants';
import { BETTING_ACTIVITY } from '../../../constants/enums';
import { USER_ROLES } from '../../../constants/keycloak';
import useFilterForm from '../../../hooks/useFilterForm';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import usePermissions from '../../../hooks/usePermissions';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormSelect from '../../molecules/FormSelect';
import PartnerSelect from '../../molecules/PartnerSelect';
import { useMarketplace } from '../../../hooks/useMarketplace';

type FilterPlayerReportData = {
  managerIds?: string[];
  agentIds?: string[];
  agentType?: Partial<UserRole>;
  bettingActivity?: string;
};

const defaultValues: FilterPlayerReportData = {
  managerIds: undefined,
  agentIds: undefined,
  agentType: undefined,
  bettingActivity: BETTING_ACTIVITY.ACTIVE,
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterPlayerReport = ({ closeModal, changeQuery }: Props) => {
  const { isAdmin } = usePermissions();
  const { hasAffiliateSystem } = useMarketplace();
  const { pathname } = useLocation();
  const page = pathname.split('/').pop();

  const prepareQueryParams = (data: Partial<FilterPlayerReportData>) => {
    const queryParams = { ...data };

    if (!isEmpty(data.agentIds)) {
      queryParams.managerIds = undefined;
    }

    return queryParams;
  };

  const { control, handleSubmit, handleResetFilter, watch, setValue } = useFilterForm<FilterPlayerReportData>({
    defaultData: defaultValues,
    changeQuery,
    prepareQueryParams,
  });

  const agentType = watch('agentType');
  const managerIds = watch('managerIds');
  const selectedAgentType = hasAffiliateSystem ? agentType : USER_ROLES.AGENT;

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (type !== 'change') return;

      if (name === 'agentType') {
        setValue('agentIds', []);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    if (hasAffiliateSystem) return;

    setValue('agentType', USER_ROLES.AGENT, { shouldDirty: true });
  }, [hasAffiliateSystem, setValue]);

  const handleClose = () => {
    closeModal?.();
  };

  const showManagerSelect = isAdmin && !hasAffiliateSystem;

  return (
    <FilterDialogLayout label={page} onSave={handleSubmit} onClose={handleClose} onReset={handleResetFilter}>
      {showManagerSelect && (
        <PartnerSelect
          name='managerIds'
          control={control}
          queryParams={{ roleTypes: [USER_ROLES.MANAGER] }}
          label='Managers'
        />
      )}
      {hasAffiliateSystem && (
        <FormSelect name='agentType' control={control} label='Partner Type' options={PARTNER_TYPE_OPTIONS} />
      )}
      {selectedAgentType && (
        <PartnerSelect
          name='agentIds'
          control={control}
          queryKey={[QUERY_KEYS.agentSearch, selectedAgentType, managerIds]}
          queryParams={{
            roleTypes: [selectedAgentType],
            filters: {
              teamLeadId: {
                values: managerIds,
              },
            },
          }}
          label={selectedAgentType === USER_ROLES.AGENT ? 'Agents' : 'Affiliates'}
        />
      )}
      <FormSelect
        name='bettingActivity'
        control={control}
        label='Betting Activity'
        options={BETTING_ACTIVITY_OPTIONS}
      />
    </FilterDialogLayout>
  );
};

export default FilterPlayerReport;
